import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Weights, OpportunityScore, ScoringParams, Opportunity } from '@/types/opportunity';
import { AssessmentContext } from '@/contexts/AssessmentContext';
import AssessmentSteps from '@/features/LegacyBenchmarksUI/components/AssessmentSteps';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/shared/button';
import { ROUTES } from '@/config/routes';
import { loadOpportunityDataFromCSV } from '@/utils/csvLoader';
import { calculateScores } from '@/utils/scoringUtils';

interface SharedAssessmentData {
  weights: Weights & { _scoringParams?: ScoringParams };
  scored_opportunities: OpportunityScore[];
  type: string;
  created_at: string;
}

const SharedOpportunityAssessment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sharedData, setSharedData] = useState<SharedAssessmentData | null>(null);

  useEffect(() => {
    const loadSharedAssessment = async () => {
      if (!id) {
        setError('Invalid share ID');
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('shared_assessments')
          .select('*')
          .eq('id', id)
          .single();

        if (fetchError) {
          console.error('Error fetching shared assessment:', fetchError);
          setError('Shared assessment not found or has expired');
          setLoading(false);
          return;
        }

        if (!data) {
          setError('Shared assessment not found');
          setLoading(false);
          return;
        }

        // Validate the data structure
        if (!data.weights || !data.scored_opportunities || data.type !== 'opportunity') {
          setError('Invalid assessment data');
          setLoading(false);
          return;
        }

        setSharedData(data);
      } catch (err) {
        console.error('Error loading shared assessment:', err);
        setError('Failed to load shared assessment');
      } finally {
        setLoading(false);
      }
    };

    loadSharedAssessment();
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-gray-600">Loading shared assessment...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Assessment Not Found</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <Button onClick={() => navigate(ROUTES.OPPORTUNITY_ASSESSMENT.ROOT)}>
            Start New Assessment
          </Button>
        </div>
      </div>
    );
  }

  if (!sharedData) {
    return null;
  }

  // Extract scoringParams from weights if they exist
  let weights: Weights;
  let scoringParams: ScoringParams = { alpha: 0.5 }; // Default

  if (sharedData.weights._scoringParams) {
    // New format with embedded scoringParams
    scoringParams = sharedData.weights._scoringParams;
    const { _scoringParams, ...cleanWeights } = sharedData.weights;
    weights = cleanWeights as Weights;
  } else {
    // Old format without scoringParams
    weights = sharedData.weights as Weights;
  }

  return (
    <SharedAssessmentProvider 
      initialWeights={weights}
      initialScoredOpportunities={sharedData.scored_opportunities}
      initialScoringParams={scoringParams}
    >
      <AssessmentSteps />
    </SharedAssessmentProvider>
  );
};

// Custom provider for shared assessments that initializes with the shared data and recalculates scores
interface SharedAssessmentProviderProps {
  children: React.ReactNode;
  initialWeights: Weights;
  initialScoredOpportunities: OpportunityScore[];
  initialScoringParams: ScoringParams;
}

const SharedAssessmentProvider: React.FC<SharedAssessmentProviderProps> = ({
  children,
  initialWeights,
  initialScoredOpportunities,
  initialScoringParams
}) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const [currentStep, setCurrentStep] = useState(2); // Start at results step
  const [weights, setWeights] = useState<Weights>(initialWeights);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [scoredOpportunities, setScoredOpportunities] = useState<OpportunityScore[]>([]);
  const [scoringParams, setScoringParams] = useState<ScoringParams>(initialScoringParams);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(true);
  const [isShowingSavedAssessment, setIsShowingSavedAssessment] = useState(false);

  // Load opportunity data from CSV on mount and recalculate scores
  useEffect(() => {
    const loadOpportunityData = async () => {
      try {
        console.log('Loading opportunity data for shared assessment...');
        const { opportunities: csvOpportunities } = await loadOpportunityDataFromCSV();
        
        if (csvOpportunities && csvOpportunities.length > 0) {
          console.log('Successfully loaded opportunities for shared assessment:', csvOpportunities.length);
          setOpportunities(csvOpportunities);
          
          // Recalculate scores with fresh opportunity data using saved weights and scoringParams
          console.log('Recalculating scores with fresh opportunity data for shared assessment');
          const recalculatedScores = calculateScores(csvOpportunities, weights, scoringParams);
          setScoredOpportunities(recalculatedScores);
          
          console.log('Shared assessment scores recalculated:', recalculatedScores.length, 'opportunities');
        } else {
          console.log('No opportunities loaded for shared assessment');
          // Fallback to the original scored opportunities if CSV loading fails
          setScoredOpportunities(initialScoredOpportunities);
        }
      } catch (error) {
        console.log('Failed to load opportunities for shared assessment:', error);
        // Fallback to the original scored opportunities if CSV loading fails
        setScoredOpportunities(initialScoredOpportunities);
      } finally {
        setIsInitialized(true);
      }
    };

    loadOpportunityData();
  }, [weights, scoringParams, initialScoredOpportunities]);

  // Function to mark assessment as having unsaved changes
  const markAsUnsaved = () => {
    setIsShowingSavedAssessment(false);
  };

  // Function to recalculate scores with current weights and scoring params
  const recalculateScores = () => {
    if (opportunities.length === 0) {
      console.log('recalculateScores - No opportunities available, skipping');
      return;
    }
    const newScores = calculateScores(opportunities, weights, scoringParams);
    setScoredOpportunities(newScores);
    markAsUnsaved();
  };

  // Helper function to load saved assessment (not used in shared view)
  const reloadSavedAssessment = async () => {
    // Not applicable for shared assessments
  };

  // Only render children once initialization is complete
  if (!isInitialized) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AssessmentContext.Provider
      value={{
        isLoading,
        hasAttemptedLoad,
        currentStep,
        setCurrentStep,
        weights,
        setWeights: (newWeights) => {
          setWeights(newWeights);
          markAsUnsaved();
        },
        opportunities,
        setOpportunities: (newOpportunities) => {
          setOpportunities(newOpportunities);
          const newScores = calculateScores(newOpportunities, weights, scoringParams);
          setScoredOpportunities(newScores);
        },
        scoredOpportunities,
        setScoredOpportunities,
        scoringParams,
        setScoringParams: (params) => {
          setScoringParams(params);
          markAsUnsaved();
        },
        recalculateScores,
        reloadSavedAssessment,
        isShowingSavedAssessment,
        markAsUnsaved,
      }}
    >
      {children}
    </AssessmentContext.Provider>
  );
};

export default SharedOpportunityAssessment;
